import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, Alert, Box, CircularProgress, Typography,
} from '@mui/material';
import { attendanceApi, AttendanceSession } from '../services/employeeService';
import { PersonAvatar } from './ui';
import { tokens } from '../theme';
import { formatDateLong, formatDuration, formatTime } from '../utils/format';

// Local datetime-local value (YYYY-MM-DDTHH:mm) from an ISO string.
const toLocalInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const REASON_MIN = 3;

export const SessionCorrectionDialog: React.FC<{
  session: AttendanceSession | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ session, onClose, onSaved }) => {
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [reasonErr, setReasonErr] = useState(false);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (session) {
      setStartedAt(toLocalInput(session.startedAt));
      setEndedAt(toLocalInput(session.endedAt));
      setReason('');
      setErr(null);
      setReasonErr(false);
    }
  }, [session]);

  const save = async () => {
    if (reason.trim().length < REASON_MIN) { setReasonErr(true); return; }
    if (!session) return;
    setBusy(true);
    setErr(null);
    try {
      await attendanceApi.adjustSession(session.id, {
        reason,
        clearReview: true,
        ...(startedAt ? { startedAt: new Date(startedAt).toISOString() } : {}),
        ...(endedAt ? { endedAt: new Date(endedAt).toISOString() } : {}),
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr((e as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się zapisać korekty. Sprawdź godziny i spróbuj ponownie.');
    } finally {
      setBusy(false);
    }
  };

  const staff = session?.staff;

  return (
    <Dialog open={!!session} onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="session-correction-title">
      <DialogTitle id="session-correction-title">Popraw godziny zmiany</DialogTitle>
      <DialogContent>
        {session && (
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{ bgcolor: tokens.ground, borderRadius: '10px', px: 1.75, py: 1.5, mb: 2 }}
          >
            {staff && <PersonAvatar firstName={staff.firstName} lastName={staff.lastName} seed={staff.id} size={36} />}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 650, fontSize: '0.9375rem' }}>
                {staff ? `${staff.firstName} ${staff.lastName}` : 'Zmiana'}
              </Typography>
              <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums', '&::first-letter': { textTransform: 'uppercase' } }}>
                {formatDateLong(session.startedAt || session.workDate)} · {session.endedAt ? `${formatTime(session.startedAt)}–${formatTime(session.endedAt)} · ${formatDuration(session.actualWorkMinutes)}` : `od ${formatTime(session.startedAt)}, zmiana niezamknięta`}
              </Typography>
            </Box>
          </Stack>
        )}
        <Stack spacing={2}>
          {err && <Alert severity="error">{err}</Alert>}
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
            <TextField label="Rozpoczęcie" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={startedAt} onChange={(e) => setStartedAt(e.target.value)} fullWidth />
            <TextField label="Zakończenie" type="datetime-local" InputLabelProps={{ shrink: true }}
              value={endedAt} onChange={(e) => setEndedAt(e.target.value)} fullWidth
              helperText={!endedAt ? 'Brak zakończenia — uzupełnij, jeśli zmiana już się skończyła.' : undefined} />
          </Box>
          <TextField
            label="Powód korekty"
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => { setReason(e.target.value); if (reasonErr && e.target.value.trim().length >= REASON_MIN) setReasonErr(false); }}
            error={reasonErr}
            helperText={reasonErr
              ? 'Korekta wymaga uzasadnienia (min. 3 znaki).'
              : 'Zapisywany w dzienniku zmian, np. „pracownica zapomniała zakończyć zmianę”.'}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>Anuluj</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Zapisz korektę
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionCorrectionDialog;
